import { useEffect, useState, useCallback } from "react";
import { api, apiError, timeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PaperPlaneRight, Megaphone, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/roles";
import { useSearchParams } from "react-router-dom";

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(["announcements", "delivery"].includes(params.get("tab")) ? params.get("tab") : "threads");
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState("");
  const [ann, setAnn] = useState({ room: "All rooms", title: "", body: "" });
  const [anns, setAnns] = useState([]);
  const [rooms, setRooms] = useState(["All rooms"]);
  const [delivery, setDelivery] = useState([]);

  const loadConvos = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConvos(data);
    setActive((a) => params.get("with") || a || data[0]?.email || null);
  }, [params]);

  const loadAnns = useCallback(async () => {
    const { data } = await api.get("/announcements");
    setAnns(data);
  }, []);

  useEffect(() => {
    loadConvos(); loadAnns(); api.get("/rooms").then(({ data }) => setRooms(["All rooms", ...data.map((r) => r.room)]));
    if (isAdmin(user)) api.get("/notifications/log").then(({ data }) => setDelivery(data));
  }, [loadConvos, loadAnns, user]);
  useEffect(() => { if (params.get("draft")) setDraft(params.get("draft")); }, [params]);

  const loadThread = useCallback(async () => {
    if (!active) return setThread([]);
    const { data } = await api.get("/messages", { params: { with_email: active } });
    setThread(data);
  }, [active]);

  useEffect(() => { loadThread(); }, [loadThread]);

  const send = async () => {
    if (!draft.trim() || !active) return;
    try {
      await api.post("/messages", { to_email: active, body: draft });
      setDraft("");
      loadThread();
      loadConvos();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const postAnn = async () => {
    try {
      await api.post("/announcements", ann);
      toast.success("Announcement sent to families");
      setAnn({ room: "All rooms", title: "", body: "" });
      loadAnns();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const delAnn = async (id) => {
    try { await api.delete(`/announcements/${id}`); loadAnns(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="messages-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Communication</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Messages</h1>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="rounded-full">
          <TabsTrigger value="threads" className="rounded-full font-bold" data-testid="tab-threads">Families</TabsTrigger>
          <TabsTrigger value="announcements" className="rounded-full font-bold" data-testid="tab-announcements">Announcements</TabsTrigger>
          {isAdmin(user) && <TabsTrigger value="delivery" className="rounded-full font-bold">Delivery</TabsTrigger>}
        </TabsList>

        <TabsContent value="threads" className="mt-6">
          <div className="grid lg:grid-cols-[20rem,1fr] gap-4">
            <div className="space-y-2" data-testid="conversation-list">
              {convos.length === 0 && <p className="text-muted-foreground">No families yet.</p>}
              {convos.map((c) => (
                <button key={c.email} onClick={() => setActive(c.email)}
                        className={`w-full text-left bg-white rounded-2xl border-2 p-4 ${
                          active === c.email ? "border-[#3B82F6]" : "border-border hover:border-blue-200"}`}
                        style={{ transition: "border-color .2s ease" }}
                        data-testid={`conversation-${c.email}`}>
                  <div className="flex justify-between items-start gap-2">
                    <p className="font-bold truncate">{c.name}</p>
                    {c.unread > 0 && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#EF4444] text-white"
                            data-testid={`unread-${c.email}`}>{c.unread}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 font-semibold truncate">
                    {(c.children || []).join(", ") || c.email}
                  </p>
                  {c.last && <p className="text-sm text-muted-foreground mt-2 truncate">{c.last}</p>}
                </button>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-border p-6 flex flex-col min-h-[28rem]">
              {!active ? (
                <p className="text-muted-foreground">Select a family to start a conversation.</p>
              ) : (
                <>
                  <p className="font-display text-xl font-semibold">
                    {convos.find((c) => c.email === active)?.name || active}
                  </p>
                  <div className="mt-5 flex-1 space-y-3 max-h-[24rem] overflow-y-auto" data-testid="thread-messages">
                    {thread.length === 0 && <p className="text-sm text-muted-foreground">No messages yet — say hello.</p>}
                    {thread.map((m) => {
                      const mine = m.from_email === user?.email;
                      return (
                        <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                          mine ? "ml-auto bg-[#3B82F6] text-white" : "bg-muted"}`}
                             data-testid={`message-${m.id}`}>
                          <p className="text-sm">{m.body}</p>
                          <p className={`text-xs mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                            {m.from_name} · {timeOf(m.at)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-5 flex gap-3">
                    <Input value={draft} onChange={(e) => setDraft(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && send()}
                           placeholder="Write a message to this family…"
                           className="h-12 rounded-xl" data-testid="message-input" />
                    <Button onClick={send} className="h-12 px-6 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                            data-testid="message-send-btn">
                      <PaperPlaneRight size={18} />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="announcements" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-border p-6 space-y-4">
              <h2 className="font-display text-xl font-semibold">New announcement</h2>
              <div>
                <Label className="text-sm font-bold">Send to</Label>
                <Select value={ann.room} onValueChange={(v) => setAnn({ ...ann, room: v })}>
                  <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="announcement-room"><SelectValue /></SelectTrigger>
                  <SelectContent>{rooms.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-bold">Title</Label>
                <Input value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })}
                       placeholder="Picture day is Friday!" className="mt-2 h-11 rounded-xl"
                       data-testid="announcement-title" />
              </div>
              <div>
                <Label className="text-sm font-bold">Message</Label>
                <Textarea value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })}
                          className="mt-2 rounded-xl min-h-28" data-testid="announcement-body" />
              </div>
              <Button onClick={postAnn} className="w-full h-12 rounded-full bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-bold"
                      data-testid="announcement-send-btn">
                <Megaphone size={18} className="mr-2" /> Send to families
              </Button>
            </div>

            <div className="space-y-3" data-testid="announcements-list">
              {anns.length === 0 && <p className="text-muted-foreground">No announcements yet.</p>}
              {anns.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl border border-border p-6" data-testid={`announcement-${a.id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div>
                      <p className="font-display text-lg font-semibold">{a.title}</p>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#3B82F6] mt-1">{a.room}</p>
                    </div>
                    {isAdmin(user) && (
                      <Button size="sm" variant="outline" onClick={() => delAnn(a.id)}
                              className="rounded-full text-[#EF4444]" data-testid={`delete-announcement-${a.id}`}>
                        <Trash size={15} />
                      </Button>
                    )}
                  </div>
                  <p className="mt-3 text-base text-muted-foreground">{a.body}</p>
                  <p className="mt-3 text-xs text-muted-foreground">{a.from_name} · {timeOf(a.at)}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {isAdmin(user) && <TabsContent value="delivery" className="mt-6 space-y-3">{delivery.filter((item) => params.get("filter") !== "failed" || !["sent", "skipped"].includes(item.email)).length === 0 && <p className="text-muted-foreground">No delivery records match this filter.</p>}{delivery.filter((item) => params.get("filter") !== "failed" || !["sent", "skipped"].includes(item.email)).map((item) => <div key={item.id} className="bg-white rounded-2xl border p-5 flex flex-wrap gap-4"><div className="flex-1"><p className="font-bold">{item.subject}</p><p className="text-sm text-muted-foreground mt-1">{item.to} · {item.at ? new Date(item.at).toLocaleString() : "—"}</p></div><span className={`text-xs font-bold rounded-full px-3 py-1.5 ${item.email === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>Email: {item.email}</span><span className="text-xs font-bold rounded-full px-3 py-1.5 bg-slate-100">SMS: {item.sms}</span></div>)}</TabsContent>}
      </Tabs>
    </div>
  );
}
